import { faThumbsUp } from '@fortawesome/pro-duotone-svg-icons/faThumbsUp';
import { faTriangleExclamation } from '@fortawesome/pro-duotone-svg-icons/faTriangleExclamation';
import { useForm } from 'antd/lib/form/Form';
import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { AddUsersFromShareUrlResponse } from 'api/project';
import { Avatar } from 'components/atoms/avatar';
import { Button } from 'components/atoms/button';
import {
  EmptyState,
  EmptyStateContainer,
  EmptyStateIcon,
} from 'components/atoms/empty-state';
import { Form } from 'components/atoms/form';
import { Input } from 'components/atoms/input';
import { Loading } from 'components/atoms/loading';
import { useAddUsersFromShareUrl } from 'hooks/customer-portal/use-customer-portal-hooks';
import { useQueryParams } from 'hooks/use-query-params';
import { ProductiveHeading03, ProductiveHeading05 } from 'ozone/atoms';
import { useAccountMetaQuery } from 'queries';
import { isValidEmail } from 'utils/is-valid-email';

const Page = styled.div`
  display: flex;
  align-items: center;
  justify-content: start;
  width: 100%;
  height: 100%;
  padding: var(--layout-06);
  flex-direction: column;
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
`;

const HeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: start;
  width: 100%;

  .ant-avatar {
    height: 68px;
    width: 68px;
  }
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: start;
  margin-left: var(--spacing-06);
`;

const WelcomeText = styled(ProductiveHeading05)``;

const LoginText = styled(ProductiveHeading03)`
  font-weight: normal;
`;

const FormWrapper = styled.div`
  margin-top: var(--layout-05);
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: start;
  ${Input} {
    width: 500px;
  }

  .ant-row.ant-form-item {
    padding-bottom: 0;
  }
`;

export const EmailInputForm: React.FC<{
  form: ReturnType<typeof useForm>[0];
  onSubmit: () => void;
  isSubmitting: boolean;
}> = ({ isSubmitting, onSubmit, form }) => {
  const { data: getMetaQueryData } = useAccountMetaQuery();

  return (
    <>
      <HeaderWrapper>
        <Avatar
          shape="square"
          size="large"
          src={getMetaQueryData?.company?.companyLogoUrl}
          name={getMetaQueryData?.company?.companyName}
        />
        <TextWrapper>
          <WelcomeText>
            <FormattedMessage id="cp.share_page.welcome" />
          </WelcomeText>
          <LoginText>
            <FormattedMessage
              id="cp.share_page.log_in"
              values={{
                companyName: getMetaQueryData?.company?.companyName,
              }}
            />
          </LoginText>
        </TextWrapper>
      </HeaderWrapper>
      <FormWrapper>
        <Form layout="vertical" form={form} onFinish={onSubmit}>
          <Form.Item
            name="email"
            label={<FormattedMessage id="cp.share_page.email_label" />}
            rules={[
              {
                required: true,
                type: 'email',
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Button loading={isSubmitting} type="primary" htmlType="submit">
            <FormattedMessage id="login" />
          </Button>
        </Form>
      </FormWrapper>
    </>
  );
};

const SuccessMessage: React.FC = () => {
  const { data: getMetaQueryData } = useAccountMetaQuery();

  return (
    <EmptyStateContainer>
      <EmptyState
        image={<EmptyStateIcon icon={faThumbsUp} />}
        title={<FormattedMessage id="cp.share_page.login.success.title" />}
        message={
          <FormattedMessage
            id="cp.share_page.login.success.message"
            values={{
              companyName: getMetaQueryData?.company?.companyName,
            }}
          />
        }
      />
    </EmptyStateContainer>
  );
};

const FailureMessage: React.FC = () => {
  const { data: getMetaQueryData } = useAccountMetaQuery();

  return (
    <EmptyStateContainer>
      <EmptyState
        image={<EmptyStateIcon icon={faTriangleExclamation} />}
        title={<FormattedMessage id="cp.share_page.login.error.title" />}
        message={
          <FormattedMessage
            id="cp.share_page.login.error.message"
            values={{
              companyName: getMetaQueryData?.company?.companyName,
            }}
          />
        }
      />
    </EmptyStateContainer>
  );
};

const Content: React.FC<{
  response: AddUsersFromShareUrlResponse | null;
  form: ReturnType<typeof useForm>[0];
  onSubmit: () => void;
  isSubmitting: boolean;
}> = ({ response, form, isSubmitting, onSubmit }) => {
  if (!response) {
    return (
      <EmailInputForm
        form={form}
        onSubmit={onSubmit}
        isSubmitting={isSubmitting}
      />
    );
  }
  if (response?.success) {
    return <SuccessMessage />;
  }

  return <FailureMessage />;
};

export const CpShareLinkPage: React.FC = () => {
  const { data: getMetaQueryData } = useAccountMetaQuery();
  const qp = useQueryParams();
  const token = useMemo(() => qp.get('token'), [qp]);
  const { mutateAsync: addUsersFromShareUrl, isLoading: isSubmitting } =
    useAddUsersFromShareUrl();
  const [form] = useForm();
  const [response, setResponse] = useState<AddUsersFromShareUrlResponse | null>(
    null,
  );

  const onSubmit = useCallback(() => {
    form.validateFields();

    const values = form.getFieldsValue();
    const emailId = values?.email;

    if (emailId) {
      if (isValidEmail(emailId) && token) {
        addUsersFromShareUrl({
          shareToken: token,
          user: {
            emailId,
          },
        }).then((res) => {
          setResponse(res);
        });
      }
    } else {
      form.validateFields();
    }
  }, [addUsersFromShareUrl, form, token]);

  return (
    <Page>
      {!getMetaQueryData ? (
        <LoadingWrapper>
          <Loading size="large" />
        </LoadingWrapper>
      ) : (
        <Content
          form={form}
          onSubmit={onSubmit}
          isSubmitting={isSubmitting}
          response={response}
        />
      )}
    </Page>
  );
};
